// Shared plumbing for the per-phase editor cards: read a value out of the
// draft document, emit a patch op for one key, and tell whether a key has a
// pending edit. Every card takes the same trio of props and builds these
// helpers the same way, so the shape lives here once.

import type { GraphNode, PhaseDocument, StrategyDocument, StrategyPatchOp } from "@/lib/types";
import { getAtPath, opKey } from "@/lib/strategyDraft";

export interface PhaseEditorProps {
  node: GraphNode;
  document: StrategyDocument;
  pendingOps: StrategyPatchOp[];
  onEdit: (op: StrategyPatchOp) => void;
}

export interface PhaseEditor {
  phase: PhaseDocument | undefined;
  read: (keyPath: string[]) => unknown;
  str: (keyPath: string[]) => string | null;
  num: (keyPath: string[]) => number | null;
  list: <T = string>(keyPath: string[]) => T[] | null;
  edit: (keyPath: string[], value: unknown) => void;
  isEdited: (keyPath: string[]) => boolean;
}

export function usePhaseEditor({ node, document, pendingOps, onEdit }: PhaseEditorProps): PhaseEditor {
  const phase = (document.strategy?.phases ?? []).find((p) => (p.id ?? p.slug) === node.slug);
  const read = (keyPath: string[]) => getAtPath(document, "phase", node.slug, keyPath);
  return {
    phase,
    read,
    str: (keyPath) => {
      const v = read(keyPath);
      return typeof v === "string" ? v : null;
    },
    num: (keyPath) => {
      const v = read(keyPath);
      return typeof v === "number" ? v : null;
    },
    list: <T,>(keyPath: string[]) => {
      const v = read(keyPath);
      return Array.isArray(v) ? (v as T[]) : null;
    },
    edit: (keyPath, value) =>
      onEdit({ scope: "phase", phase: node.slug, keyPath, value: value ?? null }),
    isEdited: (keyPath) =>
      pendingOps.some(
        (o) => opKey(o) === opKey({ scope: "phase", phase: node.slug, keyPath, value: null }),
      ),
  };
}

/** Prospect statuses a transition may set — the engine's fixed enum. */
export const PROSPECT_STATUSES = [
  "NEW", "CONTACTED", "ENGAGED", "QUALIFIED", "NEGOTIATING", "CLOSING",
  "WON", "LOST", "UNRESPONSIVE", "RE_ENGAGEMENT", "ARCHIVED",
];

/** Phases of the document as `{value,label}` options, in journey order. */
export function phaseOptions(document: StrategyDocument): { value: string; label: string }[] {
  return [...(document.strategy?.phases ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((p) => String(p.id ?? p.slug ?? ""))
    .filter((s) => s !== "")
    .map((s) => ({ value: s, label: s }));
}
